import { createContext, useContext, useState } from 'react'
import { useLocation } from 'react-router-dom'

type LegacyHooks = {
  raw: Element
  mainContent: Element | null
  navigation: Element | null
  popovers: Element[]
  others: Element[]
  href: string
}
type LegacyHooksAPI = [LegacyHooks | null, React.Dispatch<React.SetStateAction<LegacyHooks | null>>] | null

export const buildFromRoot = (root: Element, href?: string): LegacyHooks => {
  const response: LegacyHooks = {
    raw: root,
    mainContent: null,
    navigation: null,
    popovers: [],
    others: [],
    href: href ?? window.location.href
  }
  
  for (const child of [...root.children]) {
    if (child.tagName === 'MAIN') {
      response.mainContent = child
    } else if (child.tagName === 'DIV' && child.classList.contains('sidebar')) {
      response.navigation = child
    } else if (child.tagName === 'DIV' && child.classList.contains('popover') || child.classList.contains('modal')) {
      response.popovers.push(child)
    } else {
      response.others.push(child)
    }
  }

  return response
}

const LegacyPageContext = createContext<LegacyHooksAPI>(null)

export const LegacyPageProvider = ({ children, value }: { children: React.ReactNode, value: LegacyHooks }) => {
  const stateApi = useState<LegacyHooks | null>(value)
  return <LegacyPageContext.Provider value={stateApi}>{children}</LegacyPageContext.Provider>
}

export const useLegacyPage = (): LegacyHooks | null => {
  const context = useContext(LegacyPageContext)
  // ensure rerender on location change
  useLocation()
  if (context === null) {
    throw new Error('useLegacyPage must be used within a LegacyPageProvider')
  }
  const [state, setState] = context
  if (state === null || state.href !== window.location.href) {
    setState(null)
    const newLocation = window.location.href
    fetch(newLocation)
      .then(response => response.text())
      .then((data: string) => {
        const parser = new DOMParser(); 
        const document = parser.parseFromString(data, 'text/html');
        const parsed = buildFromRoot(document.body, newLocation)
        // ensure we haven't navigated away
        if (window.location.href === newLocation) {
          setState(parsed)
        }
      })
  }
  
  return state
}

export const unsetLegacyPage = () => {
  const context = useContext(LegacyPageContext)
  if (context === null) {
    return
  }
  const [state, setState] = context
  if (state === null) {
    setState(null)
  }
}